import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { carouselService, CarouselImage } from '../../services/carouselService';

const CarouselManagement: React.FC = () => {
    const { user } = useAuth();
    const [images, setImages] = useState<CarouselImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [replacing, setReplacing] = useState<string | null>(null); // Track which image is being replaced
    const [dragActive, setDragActive] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadMetadata, setUploadMetadata] = useState({
        title: '',
        subtitle: '',
        ctaText: '',
        ctaLink: ''
    });
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [imageRefreshKeys, setImageRefreshKeys] = useState<{ [key: string]: string }>({});
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadCarouselImages();
    }, []);

    // Update image refresh keys when images change
    useEffect(() => {
        if (images.length > 0) {
            setImageRefreshKeys(prev => {
                const newKeys = { ...prev };
                images.forEach(image => {
                    // Only set new timestamp if we don't have one for this image yet
                    if (!newKeys[image.filename]) {
                        newKeys[image.filename] = Date.now().toString();
                    }
                });
                return newKeys;
            });
        }
    }, [images]);

    const loadCarouselImages = async () => {
        try {
            setLoading(true);
            const response = await carouselService.getCarouselImages();
            setImages(response.images);
        } catch (error: any) {
            console.error('Erreur lors du chargement des images:', error);
        } finally {
            setLoading(false);
        }
    };

    // Check admin access
    if (user?.role !== 'admin') {
        return (
            <div className="min-h-screen bg-eldai-gray-light flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h1>
                    <p className="text-eldai-gray">Vous n'avez pas les permissions pour accéder à cette page.</p>
                    <Link to="/" className="btn btn-primary mt-4 inline-block">
                        Retour à l'accueil
                    </Link>
                </div>
            </div>
        );
    }

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
                setSelectedFile(file);
                setShowUploadForm(true);
            } else {
                alert('Veuillez sélectionner un fichier image valide.');
            }
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type.startsWith('image/')) {
                setSelectedFile(file);
                setShowUploadForm(true);
            } else {
                alert('Veuillez sélectionner un fichier image valide.');
            }
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) return;

        try {
            setUploading(true);
            const response = await carouselService.uploadCarouselImage(selectedFile, uploadMetadata);

            // Reset form
            setSelectedFile(null);
            setUploadMetadata({ title: '', subtitle: '', ctaText: '', ctaLink: '' });
            setShowUploadForm(false);
            if (fileInputRef.current) fileInputRef.current.value = '';

            // Reload images first to get the updated list
            await loadCarouselImages();

            alert('Image du carousel uploadée avec succès !');
        } catch (error: any) {
            console.error('Erreur upload:', error);
            alert(error.message || 'Erreur lors de l\'upload');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = async (filename: string) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'image "${filename}" ?`)) {
            return;
        }

        try {
            await carouselService.deleteCarouselImage(filename);

            // Remove the deleted image from refresh keys
            setImageRefreshKeys(prev => {
                const newKeys = { ...prev };
                delete newKeys[filename];
                return newKeys;
            });

            await loadCarouselImages();
            alert('Image supprimée avec succès !');
        } catch (error: any) {
            console.error('Erreur suppression:', error);
            alert(error.message || 'Erreur lors de la suppression');
        }
    };

    const handleReplaceImage = async (filename: string, file: File) => {
        if (!window.confirm(`Êtes-vous sûr de vouloir remplacer l'image "${filename}" ?`)) {
            return;
        }

        try {
            setReplacing(filename);
            await carouselService.replaceCarouselImage(filename, file);

            // Force refresh of the replaced image by updating its cache-busting key
            const newTimestamp = Date.now().toString();
            setImageRefreshKeys(prev => ({
                ...prev,
                [filename]: newTimestamp
            }));

            await loadCarouselImages();
            alert('Image remplacée avec succès !');
        } catch (error: any) {
            console.error('Erreur remplacement:', error);
            alert(error.message || 'Erreur lors du remplacement');
        } finally {
            setReplacing(null);
        }
    };

    const formatFileSize = (bytes: number) => {
        const mb = bytes / (1024 * 1024);
        return mb.toFixed(2) + ' MB';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="admin-container">
            <div className="container mx-auto px-4 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center mb-4">
                        <Link to="/admin" className="admin-nav-back">
                            <span className="admin-nav-back-icon">←</span> Retour au tableau de bord
                        </Link>
                    </div>
                    <div className="admin-header">
                        <h1 className="admin-title">
                            <span className="admin-title-icon">🎠</span> Gestion des images du carousel
                        </h1>
                        <p className="admin-subtitle">
                            Gérez les images du carousel de la page d'accueil ({images.length} images)
                        </p>
                    </div>
                </div>

                {/* Instructions */}
                <div className="admin-instructions">
                    <h3 className="admin-instructions-title">
                        📋 Instructions d'utilisation
                    </h3>
                    <div className="admin-instructions-grid">
                        <div className="admin-instructions-section">
                            <h4>📤 Upload d'images</h4>
                            <ul>
                                <li>Formats supportés: JPG, PNG, GIF, WebP</li>
                                <li>Taille maximale: 10MB par image</li>
                                <li>Résolution recommandée: 1536x1024 (ratio 3:2)</li>
                                <li>Ajoutez titre, sous-titre et bouton d'action</li>
                            </ul>
                        </div>
                        <div className="admin-instructions-section">
                            <h4>🔄 Gestion des images</h4>
                            <ul>
                                <li>Remplacez une image en gardant le même nom</li>
                                <li>Supprimez les images inutilisées</li>
                                <li>Les images sont stockées sur AWS S3</li>
                                <li>Cache automatique pour de meilleures performances</li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Upload Section */}
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h2 className="admin-card-title">📤 Ajouter une nouvelle image</h2>
                    </div>
                    <div className="admin-card-body">
                        {/* Drag & Drop Area */}
                        <div
                            className={`carousel-drag-area ${dragActive ? 'active' : ''} rounded-lg p-8 text-center transition-colors mb-6`}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                        >
                            <div className="text-6xl mb-4">🎠</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                Glissez-déposez votre image ici
                            </h3>
                            <p className="text-gray-600 mb-4">
                                Formats acceptés: JPG, PNG, GIF, WebP • Taille max: 10MB
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="admin-btn admin-btn-primary"
                            >
                                🖼️ Sélectionner un fichier
                            </button>
                        </div>

                        {/* Upload Form */}
                        {showUploadForm && selectedFile && (
                            <div className={`carousel-metadata-form relative ${uploading ? 'pointer-events-none' : ''}`}>
                                {/* Upload Overlay */}
                                {uploading && (
                                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                                        <div className="text-center">
                                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                                            <p className="text-lg font-semibold text-gray-700">Upload en cours...</p>
                                            <p className="text-sm text-gray-500">Veuillez patienter</p>
                                        </div>
                                    </div>
                                )}

                                <h4 className="text-lg font-semibold mb-4">
                                    Configuration de l'image: {selectedFile.name}
                                </h4>

                                {/* File Preview */}
                                <div className="mb-6">
                                    <div className="carousel-upload-preview">
                                        <img
                                            src={URL.createObjectURL(selectedFile)}
                                            alt="Aperçu"
                                            className="w-full"
                                        />
                                    </div>
                                    <p className="text-center text-sm text-gray-600 mt-2">
                                        Taille: {formatFileSize(selectedFile.size)}
                                    </p>
                                </div>

                                {/* Metadata Form */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">Titre</label>
                                        <input
                                            type="text"
                                            value={uploadMetadata.title}
                                            onChange={(e) => setUploadMetadata(prev => ({ ...prev, title: e.target.value }))}
                                            className="admin-form-input"
                                            placeholder="Titre principal du slide"
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">Sous-titre</label>
                                        <input
                                            type="text"
                                            value={uploadMetadata.subtitle}
                                            onChange={(e) => setUploadMetadata(prev => ({ ...prev, subtitle: e.target.value }))}
                                            className="admin-form-input"
                                            placeholder="Description du slide"
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">Texte du bouton</label>
                                        <input
                                            type="text"
                                            value={uploadMetadata.ctaText}
                                            onChange={(e) => setUploadMetadata(prev => ({ ...prev, ctaText: e.target.value }))}
                                            className="admin-form-input"
                                            placeholder="Ex: Découvrir nos produits"
                                        />
                                    </div>
                                    <div className="admin-form-group">
                                        <label className="admin-form-label">Lien du bouton</label>
                                        <input
                                            type="text"
                                            value={uploadMetadata.ctaLink}
                                            onChange={(e) => setUploadMetadata(prev => ({ ...prev, ctaLink: e.target.value }))}
                                            className="admin-form-input"
                                            placeholder="Ex: /catalog"
                                        />
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-4 justify-center">
                                    <button
                                        onClick={handleUpload}
                                        disabled={uploading}
                                        className={`admin-btn admin-btn-success ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {uploading ? (
                                            <div className="flex items-center space-x-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                <span>Upload en cours...</span>
                                            </div>
                                        ) : (
                                            '🚀 Uploader l\'image'
                                        )}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setSelectedFile(null);
                                            setShowUploadForm(false);
                                            setUploadMetadata({ title: '', subtitle: '', ctaText: '', ctaLink: '' });
                                            if (fileInputRef.current) fileInputRef.current.value = '';
                                        }}
                                        disabled={uploading}
                                        className={`admin-btn admin-btn-secondary ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        ❌ Annuler
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Images List */}
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h2 className="admin-card-title">
                            🖼️ Images du carousel ({images.length})
                        </h2>
                    </div>

                    {loading ? (
                        <div className="admin-loading">
                            <div className="admin-loading-spinner"></div>
                            <div className="admin-loading-text">Chargement...</div>
                        </div>
                    ) : images.length > 0 ? (
                        <div className="admin-card-body">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {images.map((image) => (
                                    <div key={image.filename} className={`carousel-image-card relative ${replacing === image.filename ? 'pointer-events-none opacity-75' : ''}`}>
                                        {/* Replacement Loading Overlay */}
                                        {replacing === image.filename && (
                                            <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                                                <div className="text-center">
                                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                                    <p className="text-sm font-semibold text-gray-700">Remplacement...</p>
                                                </div>
                                            </div>
                                        )}

                                        {/* Image Preview */}
                                        <div className="carousel-image-preview">
                                            <img
                                                src={`${image.url}${imageRefreshKeys[image.filename] ? `?t=${imageRefreshKeys[image.filename]}` : ''}`}
                                                alt={image.filename}
                                                loading="lazy"
                                            />
                                        </div>

                                        {/* Image Info */}
                                        <div className="carousel-image-info">
                                            <h4 className="carousel-image-filename" title={image.filename}>
                                                {image.filename}
                                            </h4>
                                            <div className="carousel-image-meta">
                                                <p>Taille: {formatFileSize(image.size)}</p>
                                                <p>Modifié: {formatDate(image.lastModified)}</p>
                                                {image.slideNumber && (
                                                    <p className="mt-2">
                                                        <span className="carousel-slide-number">
                                                            Slide #{image.slideNumber}
                                                        </span>
                                                    </p>
                                                )}
                                            </div>

                                            {/* Actions */}
                                            <div className="carousel-action-buttons mt-4">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files[0]) {
                                                            handleReplaceImage(image.filename, e.target.files[0]);
                                                        }
                                                    }}
                                                    className="hidden"
                                                    id={`replace-${image.filename}`}
                                                />
                                                <label
                                                    htmlFor={`replace-${image.filename}`}
                                                    className="admin-btn admin-btn-small admin-btn-secondary cursor-pointer"
                                                    title="Remplacer l'image"
                                                >
                                                    🔄
                                                </label>
                                                <a
                                                    href={image.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="admin-btn admin-btn-small admin-btn-secondary"
                                                    title="Voir en taille réelle"
                                                >
                                                    👁️
                                                </a>
                                                <button
                                                    onClick={() => handleDeleteImage(image.filename)}
                                                    className="admin-btn admin-btn-small admin-btn-danger"
                                                    title="Supprimer l'image"
                                                >
                                                    🗑️
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="admin-card-body">
                            <div className="carousel-empty-state">
                                <span className="emoji">🎠</span>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                                    Aucune image de carousel
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    Commencez par uploader votre première image de carousel.
                                </p>
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="admin-btn admin-btn-primary"
                                >
                                    📤 Uploader une image
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CarouselManagement;
